import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SendIcon } from 'lucide-react';
import { editorGroups } from '../../data/editorFields';
import { scenarios } from '../../data/quote';
import type { ScenarioId } from '../../types/quote';
import { easeOut } from '../../utils/motion';
import { Switch } from '../Switch';
import { FieldTag } from './FieldTag';
import { EditorFieldGroup } from './EditorFieldGroup';
type EditorPanelProps = {
  compare: boolean;
  onCompareChange: (compare: boolean) => void;
};
export function EditorPanel({
  compare,
  onCompareChange
}: EditorPanelProps) {
  const reduce = useReducedMotion();
  const [openGroup, setOpenGroup] = useState<string | null>(editorGroups[0].id);
  const [editing, setEditing] = useState<ScenarioId>('fullDoc');
  const allFields = editorGroups.flatMap(group => group.fields);
  const loCount = allFields.filter(field => field.source === 'lo').length;
  const autoCount = allFields.length - loCount;
  const activeScenario: ScenarioId = compare ? editing : 'fullDoc';
  return <motion.section aria-labelledby="lo-editor-title" initial={reduce ? false : {
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: easeOut,
    delay: 0.08
  }} className="rounded-[28px] bg-white p-5 shadow-soft" data-id="element-30" data-mp="22rhhvbzzbfzx">
      <h2 id="lo-editor-title" className="text-lg font-black" data-id="element-31" data-mp="2bqclcgbn2bzj">
        Loan Officer Editor
      </h2>
      <p className="mt-1 text-sm font-semibold text-navy-500" data-id="element-32" data-mp="2qiwld83hs3cr">Everything that feeds the quote email.</p>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-bold text-navy-500" data-id="element-33" data-mp="2lkd0zc4jqzmi">
        <span className="flex items-center gap-1.5" data-id="element-34" data-mp="2vg6hf6ldzd9k">
          <FieldTag source="lo" data-id="element-35" data-mp="2vgocw1085ngf" /> {loCount} fields
        </span>
        <span className="flex items-center gap-1.5" data-id="element-36" data-mp="1537bkkt5u630">
          <FieldTag source="auto" data-id="element-37" data-mp="155tiqjc52jkp" /> {autoCount} fields
        </span>
      </div>

      <div className="mt-4 rounded-[22px] bg-navy-50 p-4" data-id="element-38" data-mp="113q63rdqh8xu">
        <Switch checked={compare} onChange={onCompareChange} label="Compare two scenarios" description="Bank Statement vs Full Doc" data-id="element-39" data-mp="307f2hnio69ah" />
        {compare && <div role="radiogroup" aria-label="Scenario being edited" className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-white p-1" data-id="element-40" data-mp="3a4tjbo8i903b">
            {scenarios.map(scenario => {
          const selected = editing === scenario.id;
          return <button key={scenario.id} type="button" role="radio" aria-checked={selected} onClick={() => setEditing(scenario.id)} className={`h-8 whitespace-nowrap rounded-full px-3 text-xs font-extrabold duration-150 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-flame/30 motion-safe:transition-colors ${selected ? 'bg-navy text-white' : 'text-navy-500 hover:text-navy'}`} data-id="element-41" data-mp="3j3x20r3rfdha">
                  {scenario.program}
                </button>;
        })}
          </div>}
      </div>

      <div className="mt-4 space-y-2" data-id="element-42" data-mp="1r8rkiquokp1d">
        {editorGroups.map(group => <EditorFieldGroup key={group.id} group={group} scenarioId={activeScenario} open={openGroup === group.id} onToggle={() => setOpenGroup(openGroup === group.id ? null : group.id)} data-id="element-43" data-mp="1m7ka0k15lbr0" />)}
      </div>

      <div className="mt-5 border-t border-navy-100 pt-5" data-id="element-44" data-mp="29xeot7519adw">
        <button type="button" disabled aria-describedby="send-note" className="flex h-12 w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-navy-100 text-base font-extrabold text-navy-400" data-id="element-45" data-mp="255mviouhsqk2">
          <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-46" data-mp="2xok49l72a0no" />
          Send quote
        </button>
        <p id="send-note" className="mt-2 text-center text-xs font-bold text-navy-500" data-id="element-47" data-mp="2stnzrlkn1kto">
          Not wired up in this sample.
        </p>
      </div>
    </motion.section>;
}